/**
 * Creates a navigation function that preserves query parameters when navigating to a new path
 * @param navigate - React Router's navigate function
 * @param getAllParams - Function that returns all current query parameters as an object
 * @returns A function that takes a path prefix and navigates with preserved query params
 */
export const createNavigateWithQueryParams = (
  navigate: (path: string) => void,
  getAllParams: () => Record<string, string>,
) => {
  return (pathPrefix: string) => {
    const allParams = getAllParams()
    const queryString = new URLSearchParams(allParams).toString()
    const navigationPath = queryString ? `${pathPrefix}?${queryString}` : pathPrefix
    navigate(navigationPath)
  }
}

/**
 * Direct utility function for navigation with query params
 * @param navigate - React Router's navigate function
 * @param getAllParams - Function that returns all current query parameters as an object
 * @param pathPrefix - The path to navigate to (e.g., '/signup/name')
 */
export const navigateWithQueryParams = (
  navigate: (path: string) => void,
  getAllParams: () => Record<string, string>,
  pathPrefix: string,
) => {
  const allParams = getAllParams()
  const queryString = new URLSearchParams(allParams).toString()
  const navigationPath = queryString ? `${pathPrefix}?${queryString}` : pathPrefix
  navigate(navigationPath)
}

/**
 * Example usage:
 *
 * // In a React component:
 * const { getAllParams } = useQueryParams()
 * const navigate = useNavigate()
 *
 * // Method 1: Create a reusable function
 * const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)
 * navigateWithQueryParams('/signup/name') // Navigates to /signup/name?utm_campaign=test&utm_source=google
 *
 * // Method 2: Direct function call
 * navigateWithQueryParams(navigate, getAllParams, '/signup/name')
 */
